import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { CSSProperties, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { RewardUnlockedAnimation } from '@sorare/core/src/components/rewards/RewardUnlockedAnimation';
import { useSeriesNav } from '@sorare/core/src/hooks/commonGame/useSeriesNav.ts';
import { glossary } from '@sorare/core/src/lib/glossary';
import { getRewardImage } from '@sorare/core/src/lib/rewards';
import { useCommonGameSet } from '@sorare/core/src/set/index.ts';

import type { DumbRewardUnlockedStory_rewardConfig } from './__generated__/DumbRewardUnlockedStory.graphql.ts';
import { ButtonsWrapper, Root } from './styles.tsx';

type Props = {
  header?: ReactNode;
  onClose: () => void;
  title?: ReactNode;
  subtitle?: ReactNode;
  amount?: ReactNode;
  action?: ReactNode;
  rewardConfigs: DumbRewardUnlockedStory_rewardConfig[];
  rgbColor?: string;
  children?: ReactNode;
  showContinueButton?: boolean;
  hideBackground?: boolean;
  rewardPictureUrl?: string | null;
  useQuaternaryColorForAction?: boolean;
  useSetBackground?: boolean;
  disclaimer?: ReactNode;
};
export const DumbRewardUnlockedStory = ({
  header,
  onClose,
  title = <FormattedMessage defaultMessage="You've won a reward!" />,
  subtitle,
  amount,
  action,
  rewardConfigs,
  rgbColor = 'var(--c-rgb-green-400)',
  children,
  showContinueButton = true,
  hideBackground,
  rewardPictureUrl,
  useQuaternaryColorForAction = false,
  useSetBackground,
  disclaimer,
}: Props) => {
  const isSeriesNav = useSeriesNav();
  const { visual } = useCommonGameSet();
  const shouldUseSetBackground =
    useSetBackground || (isSeriesNav && !!visual.welcomeBackDialog);
  const shouldHideBackground = hideBackground || shouldUseSetBackground;
  return (
    <Root
      style={
        {
          '--rgb-color': rgbColor,
          ...(shouldUseSetBackground ? visual.welcomeBackDialog : {}),
        } as unknown as CSSProperties
      }
      className={classNames({ hideBackground: shouldHideBackground })}
    >
      {header}
      <RewardUnlockedAnimation
        imgUrls={
          rewardPictureUrl
            ? [rewardPictureUrl]
            : rewardConfigs.map(r => getRewardImage(r))
        }
        title={title}
        subtitle={subtitle}
        amount={amount}
        shouldUseSetBackground={shouldUseSetBackground}
      >
        {children}
      </RewardUnlockedAnimation>
      <ButtonsWrapper>
        {disclaimer}
        {!useQuaternaryColorForAction && action}
        {!!showContinueButton && (
          <Button
            size="large"
            onClick={() => {
              onClose?.();
            }}
            color={
              !action || useQuaternaryColorForAction ? 'primary' : 'secondary'
            }
            disableDebounce
          >
            <FormattedMessage {...glossary.continue} />
          </Button>
        )}
        {!!useQuaternaryColorForAction && action}
      </ButtonsWrapper>
    </Root>
  );
};

DumbRewardUnlockedStory.fragments = {
  rewardConfig: gql`
    fragment DumbRewardUnlockedStory_rewardConfig on AnyRewardConfigInterface {
      id
      ...getRewardImage_anyRewardConfigInterface
    }
    ${getRewardImage.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<DumbRewardUnlockedStory_rewardConfig>,
};
